"use client"

import React, { useState, useEffect } from "react"
import { fabric } from "fabric"
import { Eye, ShapesIcon, User, ArrowLeft, BrushIcon, SplineIcon, WaypointsIcon, StarIcon, UndoIcon, RedoIcon, SearchIcon, MousePointer2Icon, TrashIcon, ScissorsIcon, GridIcon, DropletIcon, SquaresUnite, LockIcon, RulerIcon, ListIcon, TargetIcon, MousePointer2Off } from "lucide-react"

import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Label } from "./ui/label"
import Shapes from "./Shapes"
import DrawSettings from './DrawSettings';
import LineSettings from './LineSettings';
import PathSettings from './PathSettings';
import { handleDrawingModes } from '../utils/handleDrawingModes';
import { editorTips } from '../utils/editorTips';
import __ from "../utils/translation"

type EditorExtrasProps = {
    canvas: fabric.Canvas | null
}

type Tool = "menu" | "shapes" | "draw" | "line" | "path"

const EditorExtras = ({ canvas }: EditorExtrasProps) => {
    const [activeTool, setActiveTool] = useState<Tool>("menu")
    const [drawSettings, setDrawSettings] = useState({
        duration: 1,
        time: 10,
        brilho: 0,
        metal: 0,
        scale: 0,
        color: 'rgba(255, 255, 255, 0)',
        camisa: true,
        short: true,
        meia: true,
        corpo: false,
        sapato: false,
        cabelo: false,
        mascara: false,
        oculos: false,
        hats: false,
        fone: false,
        polchete: false,
        morph: true,
    })
    const [selectionInfo, setSelectionInfo] = useState(editorTips[Math.floor(Math.random() * editorTips.length)])

    // on active tool change: deselect all object, handle drawing modes
    useEffect(() => {
        if (!canvas) return

        //if (activeTool !== 'select') canvas.discardActiveObject().requestRenderAll()

        handleDrawingModes(canvas, activeTool, setSelectionInfo)
    }, [canvas, activeTool])

    /* =========================
       RENDER SHAPES MENU
    ========================== */
    if (activeTool === "shapes") {
        return (
            <Card className="border-border/50 bg-card/80 backdrop-blur-sm">
                <CardHeader className="pb-3 flex flex-row items-center gap-2">
                    <ArrowLeft
                        className="h-4 w-4 cursor-pointer text-primary"
                        onClick={() => setActiveTool("menu")}
                    />
                    <CardTitle className="text-sm font-semibold uppercase tracking-wide">
                        <span className="lang">Shapes</span>
                    </CardTitle>
                </CardHeader>

                <CardContent>
                    <Shapes canvas={canvas} />
                </CardContent>
            </Card>
        )
    } else if (activeTool === "draw") {
        return (
            <Card className="border-border/50 bg-card/80 backdrop-blur-sm">
                <CardHeader className="pb-3 flex flex-row items-center gap-2">
                    <ArrowLeft
                        className="h-4 w-4 cursor-pointer text-primary"
                        onClick={() => setActiveTool("menu")}
                    />
                    <CardTitle className="text-sm font-semibold uppercase tracking-wide">
                        <span className="lang">DESENHO LIVRE</span>
                    </CardTitle>
                </CardHeader>

                <CardContent>
                    <DrawSettings canvas={canvas} />
                </CardContent>
            </Card>
        )
    } else if (activeTool === "line") {
        return (
            <Card className="border-border/50 bg-card/80 backdrop-blur-sm">
                <CardHeader className="pb-3 flex flex-row items-center gap-2">
                    <ArrowLeft
                        className="h-4 w-4 cursor-pointer text-primary"
                        onClick={() => setActiveTool("menu")}
                    />
                    <CardTitle className="text-sm font-semibold uppercase tracking-wide">
                        <span className="lang">CRIAR LINHAS</span>
                    </CardTitle>
                </CardHeader>

                <CardContent>
                    <LineSettings canvas={canvas} />
                </CardContent>
            </Card>
        )
    } else if (activeTool === "path") {
        return (
            <Card className="border-border/50 bg-card/80 backdrop-blur-sm">
                <CardHeader className="pb-3 flex flex-row items-center gap-2">
                    <ArrowLeft
                        className="h-4 w-4 cursor-pointer text-primary"
                        onClick={() => setActiveTool("menu")}
                    />
                    <CardTitle className="text-sm font-semibold uppercase tracking-wide">
                        <span className="lang">CRIAR CAMINHO </span>
                    </CardTitle>
                </CardHeader>

                <CardContent>
                    <PathSettings canvas={canvas} />
                </CardContent>
            </Card>
        )
    }

    /* =========================
       RENDER MAIN MENU
    ========================== */
    return (
        <div className="h-full">

            <Card className="border-border/50 bg-card/85 backdrop-blur-sm !h-full rounded-none">
                <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
                        <StarIcon></StarIcon>
                        <span className="lang">
                            {__("FORMAS E LINHAS")}
                        </span>
                    </CardTitle>
                </CardHeader>

                <CardContent className="grid grid-cols-2 p-2">
                    {/* SHAPES */}
                    <div className="space-y-3 m-1">
                        <div
                            className="flex items-center justify-between rounded-lg bg-muted/50 p-3 transition-colors hover:bg-primary hover:text-background cursor-pointer shadow-xl"
                            onClick={() => setActiveTool("shapes")}
                        >
                            <Label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                                <ShapesIcon />
                                <span className="lang" data-en="Shapes" data-pt="Formas" data-es="Formas">Shapes</span>
                            </Label>
                        </div>
                    </div>

                    {/* DRAW */}
                    <div className="space-y-3 m-1">
                        <div className="flex items-center justify-between rounded-lg bg-muted/50 p-3 transition-colors hover:bg-primary hover:text-background cursor-pointer shadow-xl"
                            onClick={() => setActiveTool("draw")}
                        >
                            <Label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                                <BrushIcon></BrushIcon>
                                <span className="lang" data-en="Free draw" data-pt="Desenhar" data-es="Desenhar">Free draw</span>
                            </Label>
                        </div>
                    </div>

                    {/* LINE */}
                    <div className="space-y-3 m-1">
                        <div className="flex items-center justify-between rounded-lg bg-muted/50 p-3 transition-colors hover:bg-primary hover:text-background cursor-pointer shadow-xl"
                            onClick={() => setActiveTool("line")}
                        >
                            <Label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                                <SplineIcon></SplineIcon>
                                <span className="lang" data-en="Line" data-pt="Linha" data-es="Linha">Line</span>
                            </Label>
                        </div>
                    </div>

                    {/* LINE */}
                    <div className="space-y-3 m-1">
                        <div className="flex items-center justify-between rounded-lg bg-muted/50 p-3 transition-colors hover:bg-primary hover:text-background cursor-pointer shadow-xl"
                            onClick={() => setActiveTool("path")}
                        >
                            <Label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                                <WaypointsIcon></WaypointsIcon>
                                <span className="lang" data-en="Path" data-pt="Caminho" data-es="Caminho">Path</span>
                            </Label>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}

export default EditorExtras
